import React, { FC, useState } from 'react';
import type Specialist from '../../model/Specialist';
import type { ClinicianAvailability } from '../../hooks/useClinicianAvailability';
import { formatShortDate } from '../../utils/clinician-availability';
import { parseAppointmentLengthMinutes } from '../../utils/expand-timeslots';
import { getSpecialistDisplayName } from '../../utils/specialist';
import { formatMoney, getFeeEstimate } from '../../utils/consultation-fee';

const initials = ({ firstName, lastName }: Specialist) => `${firstName?.[0] ?? ''}${lastName?.[0] ?? ''}`.toUpperCase();

// Lowest out-of-pocket amount with Medicare, as the patient has not given their cover yet.
const lowestGap = (specialist: Specialist) => {
  const estimate = getFeeEstimate(specialist.fee?.initial);
  return estimate ? formatMoney(estimate.gap.min) : null;
};

export const ClinicianAvatar: FC<{ specialist: Specialist }> = ({ specialist }) => {
  const [imageFailed, setImageFailed] = useState(false);
  return (
    <span className="av" aria-hidden>
      {specialist.avatarLink && !imageFailed
        ? <img src={specialist.avatarLink} alt="" onError={() => setImageFailed(true)} />
        : initials(specialist)}
    </span>
  );
};

export const AvailabilityTag: FC<{ availability: ClinicianAvailability }> = ({ availability }) => {
  if (availability.isLoading) {
    return <span className="tag tag-none">Checking availability…</span>;
  }
  const [first] = availability.days;
  if (!first && availability.hasError) {
    return <span className="tag tag-none">Couldn&apos;t check times</span>;
  }
  return first
    ? <span className="tag tag-ok">{`Next available ${formatShortDate(first.date)}`}</span>
    : <span className="tag tag-none">No availability</span>;
};

type OptionProps = {
  specialist: Specialist;
  // The specialty being browsed; a clinician may practise several.
  specialtyName: string;
  availability: ClinicianAvailability;
  selected: boolean;
  onSelect: () => void;
};

export const ClinicianOption: FC<OptionProps> = ({
  specialist, specialtyName, availability, selected, onSelect,
}) => {
  const from = lowestGap(specialist);
  return (
    <button type="button" className={`clinic${selected ? ' on' : ''}`} aria-pressed={selected} onClick={onSelect}>
      <ClinicianAvatar specialist={specialist} />
      <span className="who">
        <b>{getSpecialistDisplayName(specialist)}</b>
        <span className="sp">{specialtyName}</span>
        {specialist.profile?.[0] && <span className="bio">{specialist.profile[0]}</span>}
      </span>
      <span className="rt">
        <AvailabilityTag availability={availability} />
        {from && (
          <span className="from">
            {'from '}
            <b>{from}</b>
            {' out of pocket'}
          </span>
        )}
      </span>
    </button>
  );
};

type HeroProps = {
  specialist: Specialist;
  specialtyName: string;
  availability: ClinicianAvailability;
};

export const ClinicianHero: FC<HeroProps> = ({ specialist, specialtyName, availability }) => {
  const from = lowestGap(specialist);
  return (
    <div className="hero">
      <ClinicianAvatar specialist={specialist} />
      <div className="who">
        <h3>{getSpecialistDisplayName(specialist)}</h3>
        <span className="sp">{specialtyName}</span>
        <div className="tags">
          <AvailabilityTag availability={availability} />
          <span className="tag tag-none">{`Telehealth · ${parseAppointmentLengthMinutes(specialist.appointmentLength)} minutes`}</span>
        </div>
        {!!specialist.profile?.length && (
          <details>
            <summary>About this clinician</summary>
            {specialist.profile.map((paragraph) => <p key={paragraph}>{paragraph}</p>)}
          </details>
        )}
      </div>
      {from && (
        <div className="rt">
          <span className="from">
            Out of pocket from
            <b>{from}</b>
          </span>
        </div>
      )}
    </div>
  );
};

// Placeholder rows while a specialty's clinicians load, so the page keeps its shape.
export const ClinicianSkeleton: FC = () => (
  <div className="cliniclist" aria-busy="true" aria-label="Loading clinicians">
    {[0, 1, 2].map((i) => (
      <div key={i} className="clinic sk">
        <span className="av sk-block" />
        <span className="who">
          <span className="sk-line" style={{ width: '45%' }} />
          <span className="sk-line" style={{ width: '25%' }} />
          <span className="sk-line" style={{ width: '80%' }} />
        </span>
      </div>
    ))}
  </div>
);

type PickedRowProps = {
  value?: string | null;
  detail?: string | null;
  onChange: () => void;
};

// A chosen specialty or clinician, collapsed to one line so the next choice is in view.
export const PickedRow: FC<PickedRowProps> = ({ value, detail, onChange }) => (
  <div className="picked">
    <span className="ok" aria-hidden>✓</span>
    <span className="tx">
      <b>{value}</b>
      {detail && <small>{detail}</small>}
    </span>
    <button type="button" className="btn-edit" onClick={onChange}>Change</button>
  </div>
);
